import type { ToastMessageOptions } from 'primevue/toast'
import { useToast } from 'primevue/usetoast';

const lifeTime = 3000

let toastInstance: ReturnType<typeof useToast> | null = null
const recentToastUntil = new Map<string, number>()

function toastDedupeKey(options: ToastMessageOptions): string {
  return `${options.severity ?? ''}:${options.summary ?? ''}:${options.detail ?? ''}`
}

export function registerToast(toast: typeof toastInstance) {
  toastInstance = toast
  recentToastUntil.clear()
}

export function useNotification() {

  const show = (options: ToastMessageOptions) => {
    if (!toastInstance) {
      console.warn('Toast not registered')
      return
    }
    const key = toastDedupeKey(options)
    const now = Date.now()
    const expiresAt = recentToastUntil.get(key)
    if (expiresAt != null && expiresAt > now) {
      return
    }
    const life = typeof options.life === 'number' ? options.life : lifeTime
    recentToastUntil.set(key, now + life)
    toastInstance.add(options)
  }

  const success = (options: Omit<ToastMessageOptions, 'severity'>) => {
    show({ severity: 'success', life: lifeTime, ...options })
  }

  const info = (options: Omit<ToastMessageOptions, 'severity'>) => {
    show({ severity: 'info', life: lifeTime, ...options })
  }

  const warn = (options: Omit<ToastMessageOptions, 'severity'>) => {
    show({ severity: 'warn', life: lifeTime, ...options })
  }

  const error = (options: Omit<ToastMessageOptions, 'severity'>) => {
    show({ severity: 'error', life: lifeTime, ...options })
  }

  return {
    show,
    success,
    info,
    warn,
    error
  }
}
